import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Combobox, EmptyState, FormField, Input, Modal, PageHeader,
  Table, type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { Plus, Trash2 } from '../../icons';
import {
  listBudgetPeriods, createBudgetPeriod, listBudgetLines, createBudgetLine,
  deleteBudgetLine, listBudgetEntries, addBudgetEntry,
  type BudgetPeriodDto, type BudgetLineDto, type BudgetEntryDto, type BudgetAmounts,
} from '../../api/budget';
import { fetchCodeOptions } from '../../api/approval-form-sources';
import { extractErrorMessage } from '../../api/approval';
import './Budget.css';

/**
 * 예산 (2026-09-04 · 예산 판 B2).
 *
 * ## ★★★ 잔액과 «가용» 은 다르다
 *
 * 잔액 = 편성 − 집행. **가용 = 잔액 − 신청중.** 5억 예산에 3억짜리 기안 둘이 동시에 돌면
 * 잔액만 보고는 **둘 다 승인된다.** 연차가 「잔여」와 「가용」을 이름부터 가른 이유가 이것이고,
 * 예산에서는 그 사고가 훨씬 비싸다 — 그래서 표가 둘을 **나란히** 보여 준다.
 *
 * ## ★★★ 금액은 원장에만 산다
 *
 * 예산 줄에는 금액 칸이 없다. 편성액은 **원장(편성·추경·전용·이월)의 합**이고, 그래서
 * 「왜 그 숫자인가」에 언제나 답할 수 있다. 고치는 것도 덮어쓰기가 아니라 **한 줄 더하기**다.
 *
 * ## ★ 예산을 안 짠 회사도 지출결의서를 쓴다
 *
 * 여기가 비어 있어도 지출은 올라간다(판 B1). 예산은 **담는 그릇**이지 문지기가 아니다.
 */

const won = (v: number) => `${v.toLocaleString()}원`;

const PERIOD_STATUS: Record<string, { label: string; tone: 'default' | 'success' | 'warning' }> = {
  DRAFT: { label: '짜는 중', tone: 'warning' },
  OPEN: { label: '쓰는 중', tone: 'success' },
  CLOSED: { label: '닫힘', tone: 'default' },
};

const ENTRY_KIND: Record<string, string> = {
  PLAN: '편성', SUPPLEMENT: '추경', TRANSFER_IN: '전용 받음', TRANSFER_OUT: '전용 보냄', CARRYOVER: '이월',
};

/** 담당자가 손으로 넣을 수 있는 종류 — **전용은 여기 없다**(두 줄이 짝이라 따로 한다). */
const ADDABLE_KINDS = ['SUPPLEMENT', 'CARRYOVER'] as const;

export function BudgetPage() {
  const [periods, setPeriods] = useState<BudgetPeriodDto[]>([]);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [lines, setLines] = useState<BudgetLineDto[]>([]);
  const [total, setTotal] = useState<BudgetAmounts | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [error, setError] = useState<string | null>(null);

  const [newPeriod, setNewPeriod] = useState(false);
  const [newLine, setNewLine] = useState(false);
  const [ledgerOf, setLedgerOf] = useState<BudgetLineDto | null>(null);
  // 계정과목 코드 → 이름(2026-10-18 · web 레인 W9 · 평 37 «영문 계정과목»). 35 화면이 정한 이름이다 —
  // «예산 잡기» 가 이미 부르는 목록을 여기서도 받는다. 못 받거나 모르는 코드는 코드 그대로 보인다.
  const [accountNames, setAccountNames] = useState<ReadonlyMap<string, string>>(() => new Map());
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const options = await fetchCodeOptions('account_code');
        if (alive) setAccountNames(new Map((options ?? []).map((c) => [c.code, c.label] as const)));
      } catch {
        /* 이름을 못 받으면 코드를 그대로 보인다 — 표는 그대로 선다 */
      }
    })();
    return () => { alive = false; };
  }, []);
  const accountName = useCallback((code: string) => accountNames.get(code) ?? code, [accountNames]);

  const loadPeriods = useCallback(async () => {
    setState('loading');
    try {
      const list = await listBudgetPeriods();
      setPeriods(list);
      setPeriodId((cur) => cur ?? list[0]?.id ?? null);
      setState('ok');
    } catch {
      setState('folded');
    }
  }, []);

  const loadLines = useCallback(async (id: string) => {
    try {
      const res = await listBudgetLines(id);
      setLines(res.lines);
      setTotal(res.total);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => { void loadPeriods(); }, [loadPeriods]);
  useEffect(() => { if (periodId) void loadLines(periodId); }, [periodId, loadLines]);

  const period = periods.find((p) => p.id === periodId) ?? null;

  const columns: ReadonlyArray<TableColumn<BudgetLineDto>> = useMemo(() => [
    {
      key: 'accountCode', header: '계정과목', rowHeader: true,
      render: (l) => (
        <>
          {accountName(l.accountCode)}
          {l.memo ? <span className="bg__dim"> · {l.memo}</span> : null}
        </>
      ),
    },
    {
      key: 'orgKind', header: '귀속', width: '92px',
      render: (l) => (l.orgKind === 'COMPANY' ? '전사' : l.orgKind === 'DIVISION' ? '부서' : '팀'),
    },
    { key: 'planned', header: '편성', width: '128px', numeric: true, render: (l) => won(l.planned) },
    { key: 'spent', header: '집행', width: '128px', numeric: true, render: (l) => won(l.spent) },
    {
      key: 'pending', header: '신청중', width: '120px', numeric: true,
      render: (l) => <span className="bg__dim">{won(l.pending)}</span>,
    },
    {
      key: 'available', header: '가용', width: '132px', numeric: true,
      /**
       * ★★★ **가용이 이 표의 답이다.** 잔액은 결재가 도는 돈을 아직 안 뺀 값이라,
       *    그것만 보고 쓰면 같은 예산이 두 번 나간다.
       * ★★ 음수면 짚어 준다 — 다만 **막지는 않는다**(무엇을 할지는 판 B4 에서 정한다).
       */
      render: (l) => (
        <b className={l.available < 0 ? 'bg__over' : undefined}>{won(l.available)}</b>
      ),
    },
    {
      key: 'actions', header: '원장', width: '96px',
      render: (l) => (
        <Button variant="ghost" size="compact" onClick={() => setLedgerOf(l)}>
          {l.entryCount}줄
        </Button>
      ),
    },
  ], [accountName]);

  return (
    /*
     * ★★★ **바깥 상자가 있어야 한다** (2026-09-04 · 사용자 지적 「디자인이 다 깨져 있다」).
     *    결재 셸의 `.aprnav__body` 는 **스크롤 통일 뿐 여백이 없다** — 화면마다 제 상자로
     *    여백·폭·세로 간격을 잡는 것이 이 셸의 규약이다(`.asts` · `.apl` · `.hra` 가 전부 그렇다).
     *    조각(`<>`)으로 시작하면 내용이 **가장자리에 딱 붙고 절 사이 간격도 사라진다.**
     */
    <div className="bg">
      <PageHeader
        breadcrumb={crumbsFor('/approval/admin/budget')}
        scope={scopeFor('/approval/admin/budget')}
        title="예산"
        description="편성 − 집행 = 잔액, 신청중을 빼면 가용입니다 — 지금 더 쓸 수 있는 돈은 «가용» 입니다."
        actions={(
          <Button size="compact" iconLeft={<Plus size={15} aria-hidden />} onClick={() => setNewPeriod(true)}>
            기간 만들기
          </Button>
        )}
      />

      {state === 'loading' && <div className="bg__note">불러오는 중…</div>}
      {state === 'folded' && (
        <div className="bg__note">예산을 아직 볼 수 없습니다 — 서버가 이 기능을 갖추면 여기에 나타납니다.</div>
      )}
      {error && <div className="bg__err" role="alert">{error}</div>}

      {state === 'ok' && periods.length === 0 && (
        <EmptyState
          title="예산 기간이 없습니다"
          description="「2026 회계연도」처럼 기간을 먼저 만들고, 그 안에 계정과목별로 예산을 잡습니다."
          action={<Button onClick={() => setNewPeriod(true)}>기간 만들기</Button>}
        />
      )}

      {state === 'ok' && periods.length > 0 && (
        <>
          <div className="bg__bar">
            <Combobox
              value={periodId}
              onChange={setPeriodId}
              ariaLabel="예산 기간"
              options={periods.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.startDate} ~ ${p.endDate})`,
              }))}
            />
            {period && (
              <Badge variant={PERIOD_STATUS[period.status]?.tone ?? 'default'} size="sm">
                {PERIOD_STATUS[period.status]?.label ?? period.status}
              </Badge>
            )}
            <Button
              variant="secondary" size="compact"
              iconLeft={<Plus size={15} aria-hidden />}
              onClick={() => setNewLine(true)}
            >
              예산 잡기
            </Button>
          </div>

          {total && (
            <p className="bg__sum">
              편성 <b>{won(total.planned)}</b> · 집행 <b>{won(total.spent)}</b> ·
              신청중 <span className="bg__dim">{won(total.pending)}</span> ·
              가용 <b className={total.available < 0 ? 'bg__over' : undefined}>{won(total.available)}</b>
            </p>
          )}

          <Table
            caption="예산 줄 목록"
            columns={columns}
            rows={lines}
            rowKey={(l) => l.id}
            rowActions={(l) => (
              <Button
                variant="ghost" size="compact" iconOnly aria-label={`${l.accountCode} 예산 지우기`}
                onClick={async () => {
                  setError(null);
                  try {
                    await deleteBudgetLine(l.id);
                    if (periodId) await loadLines(periodId);
                  } catch (err) { setError(extractErrorMessage(err)); }
                }}
              >
                <Trash2 size={15} aria-hidden />
              </Button>
            )}
            empty={(
              <EmptyState
                title="이 기간에 잡은 예산이 없습니다"
                description="계정과목마다 한 줄씩 잡습니다 — 금액은 «원장» 에 편성으로 들어갑니다."
              />
            )}
          />
        </>
      )}

      {newPeriod && (
        <NewPeriodModal
          onClose={() => setNewPeriod(false)}
          onDone={async (id) => { setNewPeriod(false); await loadPeriods(); setPeriodId(id); }}
        />
      )}

      {newLine && periodId && (
        <NewLineModal
          periodId={periodId}
          onClose={() => setNewLine(false)}
          onDone={async () => { setNewLine(false); await loadLines(periodId); }}
        />
      )}

      {ledgerOf && (
        <LedgerModal
          line={ledgerOf}
          accountName={accountName(ledgerOf.accountCode)}
          onClose={() => setLedgerOf(null)}
          onChanged={async () => { if (periodId) await loadLines(periodId); }}
        />
      )}
    </div>
  );
}

/** 기간 만들기 — 이름과 두 날짜. */
function NewPeriodModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const year = new Date().getFullYear();
  const [name, setName] = useState(`${year} 회계연도`);
  const [startDate, setStart] = useState(`${year}-01-01`);
  const [endDate, setEnd] = useState(`${year}-12-31`);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title="예산 기간 만들기"
      onClose={onClose}
      size="sm"
      accent
      footer={(
        <div className="bg__actions">
          <Button variant="secondary" onClick={onClose}>그만두기</Button>
          <Button
            disabled={busy || name.trim() === ''}
            onClick={async () => {
              setBusy(true); setErr(null);
              try {
                const { id } = await createBudgetPeriod({ name: name.trim(), startDate, endDate });
                onDone(id);
              } catch (e) { setErr(extractErrorMessage(e)); } finally { setBusy(false); }
            }}
          >
            만들기
          </Button>
        </div>
      )}
    >
      <FormField label="이름" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
      </FormField>
      <FormField label="시작" required>
        <Input type="date" value={startDate} onChange={(e) => setStart(e.target.value)} />
      </FormField>
      <FormField label="끝" required hint="회계연도가 3월 시작이면 그 해 2월 말까지로 잡습니다.">
        <Input type="date" value={endDate} min={startDate} onChange={(e) => setEnd(e.target.value)} />
      </FormField>
      {err && <p className="bg__err" role="alert">{err}</p>}
    </Modal>
  );
}

/** 예산 잡기 — 계정과목 하나에 한 줄. 첫 편성액은 원장 한 줄이 된다. */
function NewLineModal({ periodId, onClose, onDone }: {
  periodId: string; onClose: () => void; onDone: () => void;
}) {
  const [codes, setCodes] = useState<Array<{ code: string; label: string }> | null>(null);
  const [accountCode, setCode] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchCodeOptions('account_code')
      .then((o) => { if (alive) setCodes(o); })
      .catch(() => { if (alive) setCodes([]); });
    return () => { alive = false; };
  }, []);

  const amountNum = Number(amount.replace(/,/g, ''));
  const amountOk = amount.trim() === '' || Number.isFinite(amountNum);

  return (
    <Modal
      title="예산 잡기"
      onClose={onClose}
      size="sm"
      accent
      footer={(
        <div className="bg__actions">
          <Button variant="secondary" onClick={onClose}>그만두기</Button>
          <Button
            disabled={busy || !accountCode || !amountOk}
            onClick={async () => {
              setBusy(true); setErr(null);
              try {
                await createBudgetLine(periodId, {
                  accountCode: accountCode!,
                  memo: memo.trim() || null,
                  ...(amount.trim() !== '' ? { amount: amountNum } : {}),
                });
                onDone();
              } catch (e) { setErr(extractErrorMessage(e)); } finally { setBusy(false); }
            }}
          >
            잡기
          </Button>
        </div>
      )}
    >
      {/* ★★ 목록이 비면 그 사실을 말한다 — 계정과목은 회사가 등록해야 생긴다. */}
      {codes !== null && codes.length === 0 && (
        <p className="bg__note">
          계정과목이 아직 없습니다 — 전자결재 › 계정과목에서 먼저 등록해 주세요.
        </p>
      )}
      <FormField label="계정과목" required>
        <Combobox
          value={accountCode}
          onChange={setCode}
          ariaLabel="계정과목"
          searchable
          options={(codes ?? []).map((c) => ({ value: c.code, label: c.label }))}
          placeholder={codes === null ? '불러오는 중…' : '고르세요'}
        />
      </FormField>
      <FormField
        label="편성액"
        hint="원장에 「편성」 한 줄로 들어갑니다. 나중에 고칠 때도 덮어쓰지 않고 한 줄을 더합니다."
        error={amountOk ? undefined : '숫자로 적어 주세요'}
      >
        <Input
          value={amount}
          inputMode="decimal"
          onChange={(e) => setAmount(e.target.value)}
          suffix="원"
        />
      </FormField>
      <FormField label="메모">
        <Input value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={200} />
      </FormField>
      {err && <p className="bg__err" role="alert">{err}</p>}
    </Modal>
  );
}

/**
 * 원장 — **「왜 그 숫자인가」에 답하는 자리.**
 *
 * ★★ 고치는 방법이 **덮어쓰기가 아니라 한 줄 더하기**다. 그래서 지난 판단이 안 지워진다.
 */
function LedgerModal({ line, onClose, onChanged, accountName }: {
  line: BudgetLineDto; onClose: () => void; onChanged: () => void;
  /** 계정과목 이름(W9) — 없으면 코드 */
  accountName?: string;
}) {
  const [entries, setEntries] = useState<BudgetEntryDto[] | null>(null);
  const [kind, setKind] = useState<string>('SUPPLEMENT');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await listBudgetEntries(line.id);
      setEntries(res.entries);
    } catch (e) { setErr(extractErrorMessage(e)); }
  }, [line.id]);

  useEffect(() => { void load(); }, [load]);

  const amountNum = Number(amount.replace(/,/g, ''));
  const amountOk = amount.trim() !== '' && Number.isFinite(amountNum) && amountNum !== 0;

  return (
    <Modal title={`원장 — ${accountName ?? line.accountCode}`} onClose={onClose} size="md">
      <p className="bg__note">
        편성 <b>{won(line.planned)}</b> = 아래 줄의 합입니다. 고칠 때도 덮어쓰지 않고 한 줄을 더합니다.
      </p>

      {entries === null && <p className="bg__note">불러오는 중…</p>}
      {entries && entries.length === 0 && <p className="bg__note">원장이 비어 있습니다 — 편성액이 0 입니다.</p>}
      {entries && entries.length > 0 && (
        <ul className="bg__ledger">
          {entries.map((e) => (
            <li key={e.id}>
              <span className="bg__lkind">{ENTRY_KIND[e.kind] ?? e.kind}</span>
              <span className={`bg__lamt${e.amount < 0 ? ' bg__over' : ''}`}>
                {e.amount > 0 ? '+' : ''}{won(e.amount)}
              </span>
              <span className="bg__lwhy">{e.reason ?? ''}</span>
              <span className="bg__dim">{e.at.slice(0, 10)}{e.by ? ` · ${e.by}` : ''}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="bg__add">
        <FormField label="더하기">
          <Combobox
            value={kind}
            onChange={setKind}
            ariaLabel="원장 종류"
            options={ADDABLE_KINDS.map((k) => ({ value: k, label: ENTRY_KIND[k]! }))}
          />
        </FormField>
        <FormField label="금액" hint="줄이면 음수로 적습니다.">
          <Input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} suffix="원" />
        </FormField>
        <FormField label="왜">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </FormField>
        <Button
          size="compact"
          disabled={busy || !amountOk}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              await addBudgetEntry(line.id, { kind, amount: amountNum, reason: reason.trim() || null });
              setAmount(''); setReason('');
              await load();
              onChanged();
            } catch (e) { setErr(extractErrorMessage(e)); } finally { setBusy(false); }
          }}
        >
          더하기
        </Button>
      </div>

      {/* ★ 전용은 여기서 안 한다 — 두 줄이 짝이라 한쪽만 적히면 회사 총액이 조용히 바뀐다. */}
      <p className="bg__note bg__dim">
        전용(예산을 옮기는 것)은 두 줄이 짝이라 여기서 하지 않습니다.
      </p>

      {err && <p className="bg__err" role="alert">{err}</p>}
    </Modal>
  );
}
